import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Typography,
  Stepper,
  Box,
} from '@mui/material';
import { ExpandMoreIcon } from 'components/common/Icon';
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from '@mui/icons-material';
import { Company } from 'models/company';
import {
  FormationsPlan,
  IAccount,
  ProgressTrackerStages,
  ProgressTrackerStatus,
} from 'models/account';
import { useCurrentAccount, useCurrentCompany } from 'hooks/api';
import {
  isAccountBusinessInABox,
  getCompletedCount,
  getFirstNoCompleted,
} from 'components/ProgressTracker/util';
import { FormationsStep } from 'components/FormationsStep';
import { BUSINESS_OWNERSHIP } from 'enums';
import { PersonalDetailsContainer as PersonalDetails } from './PersonalDetails';
import { CompanyDetailsContainerBusinessInABox as CompanyDetailsBusinessInABox } from './CompanyDetailsBusinessInABox';
import { OwnershipDetailsContainer as OwnershipDetails } from './OwnershipDetails';
import { AddressInformationContainer as AddressInformation } from './AddressInformation';
import { initialState, reducer } from './state';
import { StageSkeleton } from '../StageSkeleton';
import { AccordionCTA } from '../AccordionCTA';
import { FilingDocumentsBusinessInABox } from './FilingDocumentsBusinessInABox';
import { StepContentWrapper } from '../Components/StepContentWrapper';
import { StageStatusColumn, StageStatusText } from '../Components/StageStatusColumn';

interface TParams {
  company: Company;
  isDesktop: boolean;
  account: IAccount;
  expanded: boolean | string;
  handleExpand: (
    panel: string,
  ) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
  onContinueToAccountVerification: () => void;
}

const IncorporationLayout = ({
  company,
  isDesktop,
  account: accData,
  expanded,
  handleExpand,
  onContinueToAccountVerification,
}: TParams) => {
  const { t } = useTranslation();
  const isBIB = isAccountBusinessInABox(accData);
  const isMultiMemberOwnedBusiness =
    accData?.businessOwnerShip === BUSINESS_OWNERSHIP.PARTNERSHIP;
  // const addressState = company.contactDetails?.mailingAddress?.state
  // const taxFilingStatus = accData.generalTaxInfo?.taxFilingStatus
  // const isAdditionalOwnerNeeded = useMemo(() => needAdditionalOwner(addressState, taxFilingStatus), [addressState, taxFilingStatus]);

  const [state, dispatch] = useReducer(
    reducer,
    initialState(
      company?.entityType ?? company?.legacyEntityType ?? '',
      isMultiMemberOwnedBusiness,
      isBIB,
    ),
  );

  const completed = useMemo(() => {
    const localStorageCompleted =
      localStorage.getItem('filingDocumentsCompletedBIB') === 'true';
    const localStorageIncrement =
      localStorageCompleted &&
      state[ProgressTrackerStages.FilingDocuments]?.status !==
        ProgressTrackerStatus.Completed
        ? 1
        : 0;
    return getCompletedCount(state, true) + localStorageIncrement;
  }, [state, localStorage.getItem('filingDocumentsCompletedBIB')]);

  const [activeStep, setActiveStep] = useState(0);

  const onBackClick = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  useEffect(() => {
    const firstNotCompleted = getFirstNoCompleted(state, true);
    if (firstNotCompleted !== -1) {
      setActiveStep(firstNotCompleted);
    }
  }, [state]);

  useEffect(() => {
    if (accData?.progress.length > 0) {
      dispatch({
        type: ProgressTrackerStages.PersonalDetails,
        value: accData?.progress,
      });
      dispatch({
        type: ProgressTrackerStages.CompanyDetails,
        value: accData?.progress,
      });
      dispatch({
        type: ProgressTrackerStages.AddressDetails,
        value: accData?.progress,
      });
      if (isMultiMemberOwnedBusiness) {
        dispatch({
          type: ProgressTrackerStages.OwnershipDetails,
          value: accData?.progress,
        });
      }
      dispatch({
        type: ProgressTrackerStages.FilingDocuments,
        value: accData?.progress,
      });
    }
  }, [accData, isMultiMemberOwnedBusiness]);

  const steps = Object.keys(state);
  const stageCompleted = useMemo(
    () => completed === steps.length,
    [completed, steps],
  );

  return (
    <Accordion
      expanded={expanded === 'incorporation'}
      onChange={handleExpand('incorporation')}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="incorporation-content"
        id="incorporation-header"
        sx={{
          alignItems: 'flex-start',
        }}
      >
        <Grid container>
          <Grid item xs={10}>
            <Typography variant="h6B">Incorporate Your Business</Typography>
            <Typography variant="subtitle1" color="primary">
              {!stageCompleted &&
                t('progressTracker.incorporation.businessInABoxSubtitle')}
              {stageCompleted && (
                <Box>
                  {t(
                    'progressTracker.incorporation.incorporationCompletedInfo',
                  )}
                  &nbsp;
                </Box>
              )}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('progressTracker.incorporation.processingTime')}
            </Typography>
            <AccordionCTA
              expanded={expanded}
              handleExpand={handleExpand}
              completed={completed}
              totalStep={steps.length}
              testId="start-incorporation"
              accordionId="incorporation"
            />
          </Grid>

          <StageStatusColumn item xs={2}>
            <StageStatusText variant="h8B">
              {stageCompleted && <CheckCircle color="secondary" />}
              {completed}/{steps.length}
            </StageStatusText>
          </StageStatusColumn>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={{ md: 4 }}>
          <Grid
            item
            xs={12}
            md={3}
          >
            <Stepper
              activeStep={activeStep}
              nonLinear
              orientation={isDesktop ? 'vertical' : 'horizontal'}
              alternativeLabel={!isDesktop}
              data-testid="incorporation-stages"
            >
              {steps.map((key, index) => (
                <FormationsStep
                  key={key}
                  stageKey={key}
                  index={index}
                  setActiveStep={setActiveStep}
                  status={state[key]?.status}
                  label={state[key]?.label}
                  isTrainer={false}
                  completedSteps={completed}
                  totalSteps={steps.length}
                  activeStep={activeStep}
                  typeOfAccount={FormationsPlan.BIB}
                />
              ))}
            </Stepper>
          </Grid>
          <StepContentWrapper
            item
            xs={12}
            md={9}
          >
            {activeStep === 0 && <PersonalDetails company={company} />}
            {activeStep === 1 && (
              <CompanyDetailsBusinessInABox
                company={company}
                onBackClick={onBackClick}
              />
            )}
            {activeStep === 2 && accData != null && (
              <AddressInformation
                onBackClick={onBackClick}
                account={accData}
                company={company}
                hasOwnershipSection={isMultiMemberOwnedBusiness}
                onContinueToAccountVerification={
                  onContinueToAccountVerification
                }
              />
            )}
            {activeStep === 3 && accData?.id && isMultiMemberOwnedBusiness && (
              <OwnershipDetails
                account={accData}
                company={company}
                onBackClick={onBackClick}
                onContinueToAccountVerification={
                  onContinueToAccountVerification
                }
              />
            )}
            {activeStep === 3 && accData?.id && !isMultiMemberOwnedBusiness && (
              <FilingDocumentsBusinessInABox
                accountId={accData?.id}
                companyData={company}
                completedSteps={completed}
                stageCompleted={
                  state[ProgressTrackerStages.FilingDocuments]?.status ===
                  ProgressTrackerStatus.Completed
                }
                onContinueToAccountVerification={
                  onContinueToAccountVerification
                }
                onBackClick={onBackClick}
              />
            )}
            {activeStep === 4 && accData?.id && isMultiMemberOwnedBusiness && (
              <FilingDocumentsBusinessInABox
                accountId={accData?.id}
                companyData={company}
                completedSteps={completed}
                stageCompleted={
                  state[ProgressTrackerStages.FilingDocuments]?.status ===
                  ProgressTrackerStatus.Completed
                }
                onContinueToAccountVerification={
                  onContinueToAccountVerification
                }
                onBackClick={onBackClick}
              />
            )}
          </StepContentWrapper>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export const IncorporationBusinessInABox = (props: {
  isDesktop: boolean;
  expanded: boolean | string;
  handleExpand: (
    panel: string,
  ) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
  onContinueToAccountVerification: () => void;
}) => {
  const {
    currentCompany: companyData,
    isLoading: companyDataLoading,
    status: companyDataStatus,
  } = useCurrentCompany();
  const {
    currentAccount: accountData,
    isLoading: accountDataLoading,
    status: accountDataStatus,
  } = useCurrentAccount();

  return !companyDataLoading &&
    companyDataStatus === 'success' &&
    companyData &&
    !accountDataLoading &&
    accountDataStatus === 'success' &&
    accountData ? (
    <IncorporationLayout
      {...props}
      company={companyData}
      account={accountData}
    />
  ) : (
    <StageSkeleton />
  );
};
